import React, { useState, useEffect, useMemo } from 'react';
import { usePackagingOperationsContext } from '../context/PackagingOperationsContext';
import { pkgOperationsService, type SealDefectItem, type SealMachineData } from '../services/pkgOperationsService';

const STANDARD_DEFECT_TYPES = [
    "Joint Leakage",
    "Side Sealing Leakage",
    "End Sealing Leakage",
    "Fin Seal Leakage",
    "Puncture / Pin Hole",
    "Channel Leakage",
    "Wrinkle on Seal",
    "Weak Seal / Delamination",
    "Tear / Cut",
    "Micro Perforation",
    "Burnt / Melted Seal",
    "Other (Custom)"
];

const MAX_MACHINES = 10;

const createNewMachine = (index: number): SealMachineData => {
    const machineNumStr = String(index + 1).padStart(2, '0');
    return {
        id: `machine-${Date.now()}-${index}`,
        machineNo: `M/C-${machineNumStr}`,
        sampleQty: 10,
        defects: [],
        totalLeaks: 0,
        passedCount: 10,
        passPct: 100,
        passPctStr: '100.00%',
        formattedLeakageType: 'None'
    };
};

const SealIntegrity: React.FC = () => {
    const { state, dispatch } = usePackagingOperationsContext();

    // Product & SKU Selection
    const [productName, setProductName] = useState<string>(state.sessionData.site || '');
    const [sku, setSku] = useState<string>('');
    const [productOptions, setProductOptions] = useState<string[]>([]);

    // Multiple Machines State (Up to 10 machines)
    const [machines, setMachines] = useState<SealMachineData[]>([createNewMachine(0)]);
    const [activeMachineIndex, setActiveMachineIndex] = useState<number>(0);

    // Load available products from Packaging Operations service
    useEffect(() => {
        const loadProducts = async () => {
            try {
                const prods = await pkgOperationsService.fetchProducts("Packaging");
                if (prods && prods.length > 0) {
                    setProductOptions(prods);
                    if (!productName || productName === 'Rajpura') {
                        setProductName(prods[0]);
                        setSku(prods[0]);
                    }
                }
            } catch (e) {
                console.warn('Failed to load products for Seal Integrity', e);
            }
        };
        loadProducts();
    }, []);

    // When product changes, sync SKU
    const handleProductChange = (val: string) => {
        setProductName(val);
        setSku(val);
    };

    // Calculate metrics for a single machine
    const calculateMachineMetrics = (m: SealMachineData): SealMachineData => {
        const totalLeaks = m.defects.reduce((acc, cur) => acc + (Number(cur.count) || 0), 0);
        const sampleQty = Math.max(1, Number(m.sampleQty) || 10);
        const passedCount = Math.max(0, sampleQty - totalLeaks);
        const passPct = sampleQty > 0 ? (passedCount / sampleQty) * 100 : 100;
        const passPctStr = `${passPct.toFixed(2)}%`;

        let formattedLeakageType = "None";
        if (m.defects.length > 0) {
            formattedLeakageType = m.defects.map(d => {
                let label = (d.type === "Other (Custom)" || d.type === "Other")
                    ? (d.custom ? `Other (${d.custom})` : "Other")
                    : (d.custom ? `${d.type} (${d.custom})` : d.type);
                if (d.notes) label += ` [${d.notes}]`;
                return `${label}: ${d.count}`;
            }).join(", ");
        }

        return {
            ...m,
            totalLeaks,
            passedCount,
            passPct,
            passPctStr,
            formattedLeakageType
        };
    };

    // Add Machine (Up to 10)
    const handleAddMachine = () => {
        if (machines.length >= MAX_MACHINES) {
            alert(`Maximum limit of ${MAX_MACHINES} machines reached.`);
            return;
        }
        const nextIndex = machines.length;
        const newMach = createNewMachine(nextIndex);
        const updated = [...machines, newMach];
        setMachines(updated);
        setActiveMachineIndex(nextIndex);
    };

    // Remove Machine
    const handleRemoveMachine = (idxToRemove: number) => {
        if (machines.length <= 1) {
            alert("At least one machine entry must be maintained.");
            return;
        }
        const confirmed = window.confirm(`Are you sure you want to remove Machine #${idxToRemove + 1} (${machines[idxToRemove].machineNo})?`);
        if (!confirmed) return;

        const updated = machines.filter((_, idx) => idx !== idxToRemove);
        setMachines(updated);
        if (activeMachineIndex >= updated.length) {
            setActiveMachineIndex(Math.max(0, updated.length - 1));
        }
    };

    // Update active machine fields
    const updateActiveMachine = (updater: (prev: SealMachineData) => SealMachineData) => {
        setMachines(prev => {
            return prev.map((m, idx) => {
                if (idx === activeMachineIndex) {
                    const modified = updater(m);
                    return calculateMachineMetrics(modified);
                }
                return m;
            });
        });
    };

    // Add Defect Row to active machine
    const handleAddDefect = () => {
        updateActiveMachine(m => ({
            ...m,
            defects: [
                ...m.defects,
                {
                    id: `defect-${Date.now()}-${m.defects.length + 1}`,
                    type: STANDARD_DEFECT_TYPES[0],
                    custom: '',
                    count: 1,
                    notes: ''
                }
            ]
        }));
    };

    // Remove Defect Row
    const handleRemoveDefect = (defectId: string) => {
        updateActiveMachine(m => ({
            ...m,
            defects: m.defects.filter(d => d.id !== defectId)
        }));
    };

    // Update Defect Row
    const handleUpdateDefect = (defectId: string, field: keyof SealDefectItem, value: any) => {
        updateActiveMachine(m => ({
            ...m,
            defects: m.defects.map(d => {
                if (d.id === defectId) {
                    return { ...d, [field]: value };
                }
                return d;
            })
        }));
    };

    // Sync to context on changes
    useEffect(() => {
        const calculatedMachines = machines.map(calculateMachineMetrics);
        dispatch({
            type: 'SET_CHECKLIST_DATA',
            payload: {
                productName,
                sku,
                machines: calculatedMachines,
                metadata: {
                    productName,
                    sku,
                    machineNo: calculatedMachines.map(m => m.machineNo).join(", "),
                    sampleQty: calculatedMachines.reduce((a, b) => a + (b.sampleQty || 0), 0),
                    leakageCount: calculatedMachines.reduce((a, b) => a + (b.totalLeaks || 0), 0),
                    leakageType: calculatedMachines.map(m => `${m.machineNo}: ${m.formattedLeakageType}`).join(" | ")
                }
            }
        });
    }, [productName, sku, machines, dispatch]);

    // Active machine reference
    const activeMachine = machines[activeMachineIndex] || machines[0];

    // Global Aggregate Statistics
    const globalSummary = useMemo(() => {
        const totalMachines = machines.length;
        const totalTested = machines.reduce((acc, m) => acc + (Number(m.sampleQty) || 0), 0);
        const totalLeaks = machines.reduce((acc, m) => acc + (Number(m.totalLeaks) || 0), 0);
        const totalPassed = Math.max(0, totalTested - totalLeaks);
        const passPct = totalTested > 0 ? (totalPassed / totalTested) * 100 : 100;
        const deviatedMachines = machines.filter(m => m.totalLeaks > 0).map(m => m.machineNo);

        return {
            totalMachines,
            totalTested,
            totalLeaks,
            totalPassed,
            passPct: passPct.toFixed(2),
            deviatedMachines
        };
    }, [machines]);

    return (
        <div style={{ padding: '20px', fontFamily: "'Outfit', sans-serif, system-ui", color: '#1e293b' }}>
            {/* Header: Title & Product/SKU Setup */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #e2e8f0', paddingBottom: '12px', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 800, margin: 0, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ display: 'inline-block', width: '4px', height: '18px', backgroundColor: '#2563eb', borderRadius: '2px' }}></span>
                        Seal Leakage & Integrity Testing
                    </h3>
                    <p style={{ margin: '4px 0 0 12px', fontSize: '13px', color: '#64748b' }}>
                        Record and evaluate seal leakage defects across different packaging machines (up to 10 machines).
                    </p>
                </div>

                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#f1f5f9', padding: '6px 12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}>
                        Machines Configured:
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: 800, color: machines.length >= MAX_MACHINES ? '#dc2626' : '#2563eb', background: '#ffffff', padding: '2px 8px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                        {machines.length} / {MAX_MACHINES}
                    </span>
                </div>
            </div>

            {/* Product & SKU Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px', background: '#ffffff', border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Product Name <span style={{ color: '#dc2626' }}>*</span>
                    </label>
                    {productOptions.length > 0 ? (
                        <select
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 600, color: '#1e293b', background: '#ffffff', outline: 'none' }}
                            value={productName}
                            onChange={(e) => handleProductChange(e.target.value)}
                        >
                            <option value="">Select Product</option>
                            {productOptions.map((p, i) => (
                                <option key={i} value={p}>{p}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 600, color: '#1e293b', outline: 'none' }}
                            value={productName}
                            onChange={(e) => handleProductChange(e.target.value)}
                            placeholder="Enter product name"
                        />
                    )}
                </div>

                <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        SKU (Auto-Generated / Code)
                    </label>
                    <input
                        type="text"
                        style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '14px', fontWeight: 600, color: '#64748b', background: '#f8fafc', cursor: 'not-allowed' }}
                        value={sku}
                        readOnly
                        placeholder="SKU will auto-populate"
                    />
                </div>
            </div>

            {/* Machine Navigation Bar with ADD Button */}
            <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Select Machine Window:
                        </span>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>
                            (Click a tab to view/record data for that machine)
                        </span>
                    </div>

                    {/* ADD Machine Button */}
                    <button
                        type="button"
                        onClick={handleAddMachine}
                        disabled={machines.length >= MAX_MACHINES}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: machines.length >= MAX_MACHINES ? '#94a3b8' : '#2563eb',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '7px 14px',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: machines.length >= MAX_MACHINES ? 'not-allowed' : 'pointer',
                            boxShadow: machines.length >= MAX_MACHINES ? 'none' : '0 2px 4px rgba(37,99,235,0.25)',
                            transition: 'all 0.15s ease'
                        }}
                        title={machines.length >= MAX_MACHINES ? "Maximum 10 machines reached" : "Add another machine (up to 10)"}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>ADD Machine {machines.length < MAX_MACHINES ? `(${machines.length + 1} of 10)` : ''}</span>
                    </button>
                </div>

                {/* Machine Tabs List */}
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px' }}>
                    {machines.map((m, idx) => {
                        const isActive = idx === activeMachineIndex;
                        const hasLeaks = (m.totalLeaks || 0) > 0;
                        return (
                            <button
                                key={m.id || idx}
                                type="button"
                                onClick={() => setActiveMachineIndex(idx)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '8px 14px',
                                    borderRadius: '6px',
                                    border: isActive ? '2px solid #2563eb' : '1px solid #cbd5e1',
                                    background: isActive ? '#ffffff' : '#f1f5f9',
                                    color: isActive ? '#1e293b' : '#475569',
                                    fontWeight: isActive ? 800 : 600,
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    boxShadow: isActive ? '0 2px 6px rgba(37,99,235,0.15)' : 'none',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: hasLeaks ? '#dc2626' : '#16a34a' }}></span>
                                <span>{m.machineNo || `M/C-${idx + 1}`}</span>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    backgroundColor: hasLeaks ? '#fee2e2' : '#dcfce7',
                                    color: hasLeaks ? '#dc2626' : '#15803d'
                                }}>
                                    {hasLeaks ? `⚠ ${m.totalLeaks} Leaks` : '✓ 100%'}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Active Machine Container Card */}
            {activeMachine && (
                <div style={{ background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '20px', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    {/* Active Machine Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '14px', marginBottom: '18px', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '6px', fontWeight: 800, fontSize: '12px', border: '1px solid #bfdbfe' }}>
                                Machine #{activeMachineIndex + 1} of {machines.length}
                            </span>
                            <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#1e293b' }}>
                                Parameters & Defect Entry for {activeMachine.machineNo}
                            </h4>
                        </div>

                        {machines.length > 1 && (
                            <button
                                type="button"
                                onClick={() => handleRemoveMachine(activeMachineIndex)}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    backgroundColor: '#fff1f2',
                                    color: '#e11d48',
                                    border: '1px solid #fecdd3',
                                    borderRadius: '6px',
                                    padding: '5px 10px',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                                title="Remove this machine from the testing session"
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                                <span>Remove Machine #{activeMachineIndex + 1}</span>
                            </button>
                        )}
                    </div>

                    {/* Machine Number and Sample Quantity Inputs */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Machine Number <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="text"
                                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 700, color: '#1e293b', outline: 'none' }}
                                value={activeMachine.machineNo}
                                onChange={(e) => updateActiveMachine(m => ({ ...m, machineNo: e.target.value }))}
                                placeholder="e.g. M/C-01"
                            />
                        </div>

                        <div>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#475569', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Sample Quantity Tested <span style={{ color: '#dc2626' }}>*</span>
                            </label>
                            <input
                                type="number"
                                min={1}
                                style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', fontWeight: 700, color: '#1e293b', outline: 'none' }}
                                value={activeMachine.sampleQty}
                                onChange={(e) => updateActiveMachine(m => ({ ...m, sampleQty: parseInt(e.target.value, 10) || 1 }))}
                            />
                        </div>
                    </div>

                    {/* Real-time Live Pass Test % & Performance KPI Card (Replicated from Source Code) */}
                    <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', alignItems: 'center' }}>
                            {/* Summary Counts */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                                <div>
                                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block' }}>
                                        Testing Summary
                                    </span>
                                    <h6 style={{ margin: '2px 0 0 0', fontSize: '14px', fontWeight: 800, color: '#1e293b' }}>
                                        🛡️ {activeMachine.machineNo} Pass Rate
                                    </h6>
                                </div>

                                <div style={{ display: 'flex', gap: '16px', borderLeft: '2px solid #e2e8f0', paddingLeft: '16px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', fontWeight: 600 }}>Tested</span>
                                        <span style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>{activeMachine.sampleQty}</span>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', fontWeight: 600 }}>Passed</span>
                                        <span style={{ fontSize: '15px', fontWeight: 800, color: '#16a34a' }}>{activeMachine.passedCount}</span>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                        <span style={{ fontSize: '11px', color: '#64748b', display: 'block', fontWeight: 600 }}>Leakages</span>
                                        <span style={{ fontSize: '15px', fontWeight: 800, color: activeMachine.totalLeaks > 0 ? '#dc2626' : '#64748b' }}>
                                            {activeMachine.totalLeaks}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Pass % Progress Bar */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 700, color: activeMachine.totalLeaks === 0 ? '#16a34a' : '#ea580c' }}>
                                        {activeMachine.totalLeaks === 0 ? '✓ 100% Passed (Optimal)' : `⚠ Deviation Detected (${activeMachine.totalLeaks} Leaks)`}
                                    </span>
                                    <span style={{
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: activeMachine.passPct === 100 ? '#dcfce7' : activeMachine.passPct >= 80 ? '#fef3c7' : '#fee2e2',
                                        color: activeMachine.passPct === 100 ? '#15803d' : activeMachine.passPct >= 80 ? '#b45309' : '#b91c1c'
                                    }}>
                                        {activeMachine.passPctStr} PASS
                                    </span>
                                </div>
                                <div style={{ height: '10px', backgroundColor: '#e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                                    <div
                                        style={{
                                            height: '100%',
                                            width: `${Math.min(100, Math.max(0, activeMachine.passPct))}%`,
                                            backgroundColor: activeMachine.passPct === 100 ? '#16a34a' : activeMachine.passPct >= 80 ? '#f59e0b' : '#dc2626',
                                            transition: 'width 0.3s ease'
                                        }}
                                    ></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Defect Observations Section (Source Code Defect Builder) */}
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                            <div>
                                <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#1e293b' }}>
                                    🔍 Leakage / Defect Observations ({activeMachine.machineNo})
                                </h4>
                                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                    Add all defect types and counts observed for this machine. If zero leakages, leave empty (100% Pass).
                                </p>
                            </div>

                            <button
                                type="button"
                                onClick={handleAddDefect}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#eff6ff',
                                    color: '#2563eb',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '6px',
                                    padding: '6px 14px',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                <span>Add Defect Observation</span>
                            </button>
                        </div>

                        {/* Defect Rows Table */}
                        {activeMachine.defects.length > 0 ? (
                            <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', textAlign: 'left' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                            <th style={{ padding: '10px 12px', width: '5%', textAlign: 'center', fontWeight: 700, color: '#475569' }}>#</th>
                                            <th style={{ padding: '10px 12px', width: '25%', fontWeight: 700, color: '#475569' }}>Leakage / Defect Type</th>
                                            <th style={{ padding: '10px 12px', width: '28%', fontWeight: 700, color: '#475569' }}>Custom Defect Details</th>
                                            <th style={{ padding: '10px 12px', width: '14%', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Defect Count</th>
                                            <th style={{ padding: '10px 12px', width: '18%', fontWeight: 700, color: '#475569' }}>Notes / Position</th>
                                            <th style={{ padding: '10px 12px', width: '10%', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeMachine.defects.map((d, dIdx) => {
                                            const isOther = d.type === "Other (Custom)";
                                            return (
                                                <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9', background: dIdx % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                                                    <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#64748b' }}>
                                                        {dIdx + 1}
                                                    </td>
                                                    <td style={{ padding: '8px 12px' }}>
                                                        <select
                                                            style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 600, color: '#1e293b', outline: 'none' }}
                                                            value={d.type}
                                                            onChange={(e) => handleUpdateDefect(d.id, 'type', e.target.value)}
                                                        >
                                                            {STANDARD_DEFECT_TYPES.map((t, tIdx) => (
                                                                <option key={tIdx} value={t}>{t}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{ padding: '8px 12px' }}>
                                                        <input
                                                            type="text"
                                                            style={{
                                                                width: '100%',
                                                                padding: '6px 10px',
                                                                borderRadius: '6px',
                                                                border: isOther && !d.custom ? '1px solid #f59e0b' : '1px solid #cbd5e1',
                                                                fontSize: '13px',
                                                                outline: 'none',
                                                                backgroundColor: isOther ? '#fffbeb' : '#ffffff'
                                                            }}
                                                            placeholder={isOther ? 'Required: Enter custom description...' : 'Custom detail (optional)'}
                                                            value={d.custom || ''}
                                                            onChange={(e) => handleUpdateDefect(d.id, 'custom', e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            style={{ width: '80px', padding: '6px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', fontWeight: 700, textAlign: 'center', outline: 'none' }}
                                                            value={d.count}
                                                            onChange={(e) => handleUpdateDefect(d.id, 'count', parseInt(e.target.value, 10) || 1)}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px 12px' }}>
                                                        <input
                                                            type="text"
                                                            style={{ width: '100%', padding: '6px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                                                            placeholder="e.g. Bottom fold, Pack 3"
                                                            value={d.notes || ''}
                                                            onChange={(e) => handleUpdateDefect(d.id, 'notes', e.target.value)}
                                                        />
                                                    </td>
                                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveDefect(d.id)}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                backgroundColor: '#fee2e2',
                                                                color: '#dc2626',
                                                                border: '1px solid #fca5a5',
                                                                borderRadius: '6px',
                                                                padding: '5px 10px',
                                                                fontSize: '12px',
                                                                fontWeight: 700,
                                                                cursor: 'pointer'
                                                            }}
                                                            title="Delete Defect Observation"
                                                        >
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                <polyline points="3 6 5 6 21 6"></polyline>
                                                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                            </svg>
                                                            <span>Delete</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            /* Empty State when 0 defects (Replicated from Source Code) */
                            <div style={{ textAlign: 'center', padding: '24px 16px', backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px' }}>
                                <div style={{ fontSize: '24px', marginBottom: '6px' }}>🛡️</div>
                                <span style={{ color: '#16a34a', fontWeight: 800, fontSize: '14px', display: 'block' }}>
                                    No Leakage Defects Added for {activeMachine.machineNo} (100.00% Pass Test)
                                </span>
                                <span style={{ color: '#64748b', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                    If any seal leakage or pin holes were identified during inspection, click <strong>"+ Add Defect Observation"</strong> above.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Consolidated Overall Multi-Machine Summary Card */}
            <div style={{ background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '8px', padding: '16px 20px', marginTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Consolidated Inspection Overview
                        </span>
                        <h5 style={{ margin: '2px 0 0 0', fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
                            All Machines Tested ({globalSummary.totalMachines} Machines)
                        </h5>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                        <div>
                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Total Tested</span>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a' }}>{globalSummary.totalTested} samples</span>
                        </div>
                        <div>
                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Total Passed</span>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: '#16a34a' }}>{globalSummary.totalPassed}</span>
                        </div>
                        <div>
                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Total Leakages</span>
                            <span style={{ fontSize: '14px', fontWeight: 800, color: globalSummary.totalLeaks > 0 ? '#dc2626' : '#16a34a' }}>
                                {globalSummary.totalLeaks}
                            </span>
                        </div>
                        <div>
                            <span style={{ fontSize: '11px', color: '#64748b', display: 'block' }}>Overall Pass %</span>
                            <span style={{
                                fontSize: '13px',
                                fontWeight: 800,
                                padding: '2px 8px',
                                borderRadius: '4px',
                                backgroundColor: Number(globalSummary.passPct) === 100 ? '#dcfce7' : Number(globalSummary.passPct) >= 80 ? '#fef3c7' : '#fee2e2',
                                color: Number(globalSummary.passPct) === 100 ? '#15803d' : Number(globalSummary.passPct) >= 80 ? '#b45309' : '#b91c1c'
                            }}>
                                {globalSummary.passPct}% PASS
                            </span>
                        </div>
                    </div>
                </div>

                {globalSummary.deviatedMachines.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#fee2e2', border: '1px solid #fecdd3', borderRadius: '6px', fontSize: '12.5px', color: '#991b1b', fontWeight: 600 }}>
                        ⚠ Deviations detected on machine(s): <strong>{globalSummary.deviatedMachines.join(', ')}</strong>.
                        Submitting will route these records to Production Corrective Action.
                    </div>
                )}
            </div>
        </div>
    );
};

export default SealIntegrity;
